import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GitCommit, RefreshCw, FolderOpen, Link2, Unlink, Undo2 } from 'lucide-react';
import Tooltip from '../ui/Tooltip';
import useTaskCommits from './useTaskCommits';
import CodeCommitRow from '../git/CodeCommitRow';
import PickLinkModal from '../git/PickLinkModal';
import { branchNameFor } from '../../extensions/bundled/task-manager/taskKeys';
import { commitLinksOf, withCommitLinked, withCommitUnlinked, withCommitUnhidden } from '../../extensions/bundled/task-manager/taskCommitLinks';
import { setClonePath, sameRepository } from '../../services/git/codeRepoClones';
import { loadCodeRepoLog } from '../../services/git/codeRepoCommits';
import { useNotificationStore } from '../../store/notificationStore';
import { useAuthStore } from '../../store/authStore';
import './TaskCommits.css';

/** The folder of a linked-by-URL repository on this device: picked once, checked against its origin. */
async function chooseCloneFolder(repo, t) {
  const { open } = await import('@tauri-apps/plugin-dialog');
  const picked = await open({ directory: true, multiple: false });
  if (!picked || typeof picked !== 'string') return false;
  const notify = useNotificationStore.getState();
  try {
    const log = await loadCodeRepoLog(picked, { refresh: true });
    if (repo.remote && !sameRepository(repo.remote, log.remoteUrl)) {
      notify.error(t('taskLinks.commits.wrongFolder'), repo.name);
      return false;
    }
  } catch (e) {
    notify.error(String(e?.message || e), repo.name);
    return false;
  }
  return setClonePath(repo.id, picked);
}

/** "abc1234 Fix login — app · main": what the commit picker searches (sha prefix or message). */
export const commitPickLabel = (c) => `${String(c.oid).slice(0, 7)} ${c.summary || ''} — ${c.repo?.name || ''}${c.branch ? ` · ${c.branch}` : ''}`;
const commitPickKey = (c) => `${c.repo?.id}:${c.oid}`;

function RowButton({ label, onClick, children, testId }) {
  return (
    <Tooltip content={label} side="top">
      <button type="button" className="task-commit-open" onClick={onClick} aria-label={label} data-testid={testId}>
        {children}
      </button>
    </Tooltip>
  );
}

/**
 * The task window's "Commits" section (commit ↔ task links): the commits of
 * the board's project repositories whose message or branch names this task's
 * key (or an old key of a renamed board), plus the ones linked by hand —
 * newest first, read from the local clones. "Link commit" picks one from those
 * clones; Unlink removes a hand-made link or hides an automatic one.
 * `onChange` is the task's patch writer (it stores `commitLinks`); without it
 * the section is read-only.
 */
export default function TaskCommits({ taskKey, aliasKeys = [], title, boardId, task = null, onChange = null }) {
  const { t } = useTranslation();
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const links = commitLinksOf(task);
  const { desktop, projectIds, repos, states, commits, hidden, allCommits, loading, truncated, refresh } = useTaskCommits({
    boardId,
    taskKeys: [taskKey, ...aliasKeys],
    commitLinks: links,
  });
  const [picking, setPicking] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  const branch = branchNameFor(taskKey, title);
  const canEdit = !!onChange && !!task;

  const link = (commit) => onChange({ commitLinks: withCommitLinked(task, commit, { userId }) });
  const unlink = (commit) =>
    onChange({ commitLinks: withCommitUnlinked(task, commit, { automatic: commit.linkedBy === 'message' || commit.linkedBy === 'branch', userId }) });
  const unhide = (commit) => onChange({ commitLinks: withCommitUnhidden(task, commit) });

  let body = null;
  if (!desktop) body = <p className="task-commits-note">{t('taskLinks.commits.desktopOnly')}</p>;
  else if (!projectIds.length) body = <p className="task-commits-note">{t('taskLinks.commits.noProject')}</p>;
  else if (!loading && !repos.length) body = <p className="task-commits-note">{t('taskLinks.commits.noRepo')}</p>;

  const problems = desktop
    ? repos.map((r) => ({ r, st: states[r.id] })).filter(({ st }) => st && (st.status === 'noClone' || st.status === 'error'))
    : [];
  const shownKeys = new Set(commits.map(commitPickKey));
  const pickable = allCommits.filter((c) => !shownKeys.has(commitPickKey(c)));

  return (
    <section className="task-modal-section task-commits" aria-label={t('taskLinks.commits.title')}>
      <span className="task-modal-label">
        <GitCommit size={13} aria-hidden />
        {t('taskLinks.commits.title')}
        {commits.length > 0 && <span className="task-detail-comments-count">{commits.length}</span>}
        {desktop && canEdit && allCommits.length > 0 && (
          <button type="button" className="task-commits-link" onClick={() => setPicking(true)} data-testid="task-commits-link">
            <Link2 size={12} aria-hidden />
            {t('taskLinks.commits.linkCommit')}
          </button>
        )}
        {desktop && repos.length > 0 && (
          <Tooltip content={t('taskLinks.commits.refresh')} side="top">
            <button type="button" className="task-commits-refresh" onClick={refresh} aria-label={t('taskLinks.commits.refresh')}>
              <RefreshCw size={12} aria-hidden />
            </button>
          </Tooltip>
        )}
      </span>
      {body}
      {problems.map(({ r, st }) => (
        <div key={r.id} className="task-commits-problem" data-repo-state={st.status === 'error' ? st.kind : 'noClone'}>
          <span>
            {st.status === 'noClone'
              ? t('taskLinks.commits.notOnDevice', { name: r.name })
              : st.kind === 'missing'
                ? t('taskLinks.commits.missing', { name: r.name })
                : st.kind === 'notGit'
                  ? t('taskLinks.commits.notGit', { name: r.name })
                  : t('taskLinks.commits.error', { name: r.name, error: st.error })}
          </span>
          {r.remote && (st.status === 'noClone' || st.kind === 'missing') && (
            <button
              type="button"
              className="task-commits-choose"
              onClick={async () => {
                if (await chooseCloneFolder(r, t)) refresh();
              }}
            >
              <FolderOpen size={12} aria-hidden />
              {t('taskLinks.commits.chooseFolder')}
            </button>
          )}
        </div>
      ))}
      {!body && loading && commits.length === 0 && <p className="task-commits-note" role="status">{t('taskLinks.commits.loading')}</p>}
      {!body && !loading && commits.length === 0 && repos.length > 0 && problems.length < repos.length && (
        <p className="task-commits-note">{t('taskLinks.commits.none', { key: taskKey, branch })}</p>
      )}
      {commits.length > 0 && (
        <ul className="task-commits-list">
          {commits.map((c) => (
            <CodeCommitRow
              key={commitPickKey(c)}
              commit={c}
              actions={
                canEdit && (
                  <RowButton label={t('taskLinks.commits.unlink')} onClick={() => unlink(c)} testId="task-commit-unlink">
                    <Unlink size={13} aria-hidden />
                  </RowButton>
                )
              }
            />
          ))}
        </ul>
      )}
      {hidden.length > 0 && (
        <button type="button" className="task-commits-hidden-toggle" onClick={() => setShowHidden((v) => !v)} aria-expanded={showHidden}>
          {t('taskLinks.commits.hiddenCount', { count: hidden.length })}
        </button>
      )}
      {showHidden && hidden.length > 0 && (
        <ul className="task-commits-list task-commits-list--hidden">
          {hidden.map((c) => (
            <CodeCommitRow
              key={commitPickKey(c)}
              commit={c}
              actions={
                canEdit && (
                  <RowButton label={t('taskLinks.commits.linkAgain')} onClick={() => unhide(c)} testId="task-commit-unhide">
                    <Undo2 size={13} aria-hidden />
                  </RowButton>
                )
              }
            />
          ))}
        </ul>
      )}
      {truncated && commits.length > 0 && <p className="task-commits-note">{t('taskLinks.commits.truncated')}</p>}
      {picking && (
        <PickLinkModal
          title={t('taskLinks.commits.linkCommitTitle', { key: taskKey })}
          options={pickable}
          getKey={commitPickKey}
          getLabel={commitPickLabel}
          placeholder={t('taskLinks.commits.pickPlaceholder')}
          searchPlaceholder={t('taskLinks.commits.pickSearch')}
          emptyText={t('taskLinks.commits.pickEmpty')}
          noMatchText={t('taskLinks.commits.pickNoMatch')}
          submitLabel={t('taskLinks.commits.linkSubmit')}
          onPick={(c) => link(c)}
          onClose={() => setPicking(false)}
        />
      )}
    </section>
  );
}
